import * as React from 'react';
import { ServerCrash, RotateCcw } from 'lucide-react';

import { Button } from '@/components/ui/button.tsx';
import { usePostLogErrorToBackend } from '@/features/feedback/hooks/mutations/use-post-log-error-to-backend.ts';

interface TableErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface TableErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

function TableErrorFallback({ error, onRetry }: { error: Error | null; onRetry: () => void }) {
  const { mutate: logError } = usePostLogErrorToBackend();

  React.useEffect(() => {
    if (error) {
      logError(error);
    }
  }, [error, logError]);

  return (
    <div className="rounded-md border border-red-200 bg-white">
      <div className="flex flex-col items-center justify-center gap-4 px-6 py-16 text-center">
        <div className="bg-muted flex h-16 w-16 items-center justify-center rounded-2xl">
          <ServerCrash className="text-muted-foreground h-8 w-8" strokeWidth={1.5} />
        </div>

        <div className="flex flex-col gap-1.5">
          <p className="text-foreground text-sm font-semibold">Failed to load table data</p>
          <p className="text-muted-foreground max-w-sm text-sm">
            We&apos;ve detected an issue and our team has been automatically notified.
            We&apos;ll resolve this as quickly as possible while you continue using our
            application.
          </p>
        </div>

        <Button variant="outline" size="sm" onClick={onRetry} className="mt-1 gap-2">
          <RotateCcw className="h-3.5 w-3.5" />
          Try again
        </Button>
      </div>
    </div>
  );
}

class TableErrorBoundary extends React.Component<
  TableErrorBoundaryProps,
  TableErrorBoundaryState
> {
  constructor(props: TableErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): TableErrorBoundaryState {
    return { hasError: true, error };
  }

  handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  override render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return <TableErrorFallback error={this.state.error} onRetry={this.handleReset} />;
    }

    return this.props.children;
  }
}

export { TableErrorBoundary };
